<template>
  <div class="text-center">
    <v-dialog v-model="bookingDetailDialog.visibility" max-width="640" scrollable>
      <v-card class="pa-0" flat>
        <!-- Header -->
        <v-toolbar dark color="rgb(47, 207, 156)" dense>
          <v-btn icon disabled></v-btn>
          <v-spacer></v-spacer>
          <v-toolbar-title>Booking Detail</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="SET_BOOKING_DETAIL_DIALOG_VISIBILITY(false)">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text v-if="booking" class="pt-4">
          <!-- Booking Information -->
          <div class="text-subtitle-2 font-weight-bold mb-1">Booking</div>
          <v-simple-table dense class="mb-4">
            <template v-slot:default>
              <tbody>
                <tr>
                  <td class="font-weight-medium" style="width: 45%">Userform Submit ID</td>
                  <td>{{ display(booking.booking_id) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Customer Name</td>
                  <td>{{ display(booking.lastname) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Enquiry Option</td>
                  <td>{{ display(booking.enquiry_option) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Booking Date</td>
                  <td>{{ display(booking.date) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Booked Timeslot</td>
                  <td>{{ bookedTimeslot }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Contact Number</td>
                  <td>{{ contactNumber }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Email Address</td>
                  <td>{{ display(booking.email) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Policy Number</td>
                  <td>{{ display(booking.policy_number) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Profile</td>
                  <td>{{ display(booking.profile_name) }}</td>
                </tr>
                <!-- The queue this booking was imported into. Kept separate from the live
                     routing row below, which the backend recomputes from the current time. -->
                <tr>
                  <td class="font-weight-medium">Service Type (Booked)</td>
                  <td>{{ bookedServiceTypeDisplay(booking) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Dedicated Staff</td>
                  <td>{{ staffDisplay(booking.dedicated_staff_id) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Window Status</td>
                  <td>
                    <v-chip x-small label :color="windowStatusColor(booking.booking_window_status)" dark>
                      {{ windowStatusLabel(booking.booking_window_status) }}
                    </v-chip>
                  </td>
                </tr>
                <tr v-if="booking.booking_window_start || booking.booking_window_end">
                  <td class="font-weight-medium">Window Range</td>
                  <td>{{ display(booking.booking_window_start) }} ~ {{ display(booking.booking_window_end) }}</td>
                </tr>
                <!-- Persisted when the ticket was issued; blank until then. The live preview
                     lives on its own row so the two are never mistaken for each other. -->
                <tr>
                  <td class="font-weight-medium">
                    Ticket Decision
                    <div v-if="unrecordedTicketDecision" class="text-caption grey--text">Not recorded — this ticket predates the booking/walk-in decision column</div>
                  </td>
                  <td>{{ ticketDecisionLabel(booking.ticket_decision) }}</td>
                </tr>
                <tr v-if="showLiveRouting">
                  <td class="font-weight-medium">
                    If checked in now
                    <div class="text-caption grey--text">Recalculated from the current time; no ticket issued yet</div>
                  </td>
                  <td>{{ liveServiceTypeDisplay(booking) }} &middot; {{ ticketDecisionLabel(booking.effective_ticket_decision) }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>

          <!-- Linked Ticket Information -->
          <div class="text-subtitle-2 font-weight-bold mb-1">Linked Ticket</div>
          <div v-if="unreferencedTicket" class="text-body-2 grey--text pb-2">
            A ticket was created for this booking before the ticket reference columns existed, so no ID or code was recorded for it.
          </div>
          <v-simple-table dense v-else-if="hasTicket">
            <template v-slot:default>
              <tbody>
                <tr>
                  <td class="font-weight-medium" style="width: 45%">Ticket ID</td>
                  <td>{{ display(booking.ticket_id) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Ticket User ID</td>
                  <td>{{ display(booking.ticket_user_id) }}</td>
                </tr>
                <tr v-if="booking.ticket_code">
                  <td class="font-weight-medium">Ticket Code</td>
                  <td>{{ display(booking.ticket_code) }}</td>
                </tr>
                <tr>
                  <td class="font-weight-medium">Created At</td>
                  <td>{{ display(booking.ticket_created_datetime) }}</td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <div v-else class="text-body-2 grey--text pb-2">No ticket has been created for this booking yet.</div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapMutations, mapState } from 'vuex'
import bookingDisplay from '@/mixins/bookingDisplay'
export default {
  name: 'bookingDetailDialog',
  mixins: [bookingDisplay],
  computed: {
    ...mapState(['bookingDetailDialog', 'general']),
    booking() {
      return this.bookingDetailDialog.selected_booking
    },
    hasTicket() {
      return this.hasIssuedTicket(this.booking)
    },
    // Ticketed, but by the pre-v2 flow that recorded no reference at all. The table below would
    // otherwise render three placeholder rows and say nothing about why.
    unreferencedTicket() {
      const booking = this.booking
      return this.hasTicket && !(booking.ticket_id || booking.ticket_user_id || booking.ticket_code)
    },
    // A booking ticketed before the v2 `ticket_decision` column existed: the flag says a ticket
    // was issued, the column is null, and no live recalculation can tell us what was decided.
    unrecordedTicketDecision() {
      return this.hasTicket && !(this.booking && this.booking.ticket_decision)
    },
    // `effective_*` is derived by the backend from the clock at request time, so it answers a
    // different question once a ticket exists ("where would it go now" vs "where did it go").
    // Offer it only while it is still the operative answer. The backend does not expose the
    // issued ticket's own service type, so there is nothing to show in its place afterwards.
    showLiveRouting() {
      const booking = this.booking
      if (!booking || this.hasTicket) return false
      return !!(booking.effective_service_type_id || booking.effective_ticket_decision)
    },
    // "14:30 - 15:00". The slot end is computed by the backend (bookings stores the
    // start only), so the panel never re-derives it.
    bookedTimeslot() {
      const booking = this.booking
      if (!booking) return '-'
      if (booking.booking_timeslot) return booking.booking_timeslot
      if (booking.time && booking.booking_end_time) return `${booking.time} - ${booking.booking_end_time}`
      return this.display(booking.time)
    },
    // Area code and number are separate columns in the uploaded record; the number is
    // already masked server-side, so it is rendered exactly as received.
    contactNumber() {
      const booking = this.booking
      if (!booking || !booking.tel) return '-'
      return booking.area_code ? `+${String(booking.area_code).replace(/^\+/, '')} ${booking.tel}` : booking.tel
    }
  },
  methods: {
    ...mapMutations(['SET_BOOKING_DETAIL_DIALOG_VISIBILITY']),
    display(value) {
      return value === null || typeof value === 'undefined' || value === '' ? '-' : value
    },
    staffDisplay(staff_id) {
      if (!staff_id) return '-'
      const user = this.general.users.find(u => u.user_id === staff_id)
      if (!user) return staff_id
      return `${user.firstname} ${user.lastname} (#${staff_id})`
    }
  }
}
</script>
